import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { X } from 'lucide-react';
import { leadership } from '../mock';

export default function ExecutiveLeadership() {
  const [active, setActive] = useState(null);

  return (
    <section id="leadership" className="py-24 md:py-28 px-6 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block mb-6 pb-1 border-b border-[#b39869]">
            <span className="tracking-[0.35em] text-[11px] text-[#b39869] uppercase">Leadership</span>
          </div>
          <h2
            className="text-4xl md:text-5xl text-[#1a1a1a] mb-6 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}
          >
            Meet The Collective
          </h2>
          <p className="text-[#555] text-sm md:text-base max-w-2xl mx-auto">
            A private collective of advisors setting a higher standard — built for those who value depth,
            integrity, and elevated thinking.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {leadership.map((p) => (
            <button
              key={p.name}
              onClick={() => setActive(p)}
              className="group text-left"
              aria-label={`Read bio for ${p.name}`}
            >
              <div className="relative overflow-hidden aspect-[3/4] mb-5 bg-[#dcd6c9]">
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors duration-500 flex items-end justify-center pb-10">
                  <span className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500 bg-[#b39869] text-white uppercase tracking-[0.32em] text-[11px] px-8 py-3">
                    Learn More
                  </span>
                </div>
              </div>
              <h4
                className="text-xl md:text-2xl text-[#1a1a1a] leading-tight"
                style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}
              >
                {p.name}
              </h4>
              <p className="text-[#7a6b4e] text-[11px] tracking-[0.25em] uppercase mt-2">{p.role}</p>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-white border-0 max-h-[90vh]">
          {active && (
            <div className="grid md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] max-h-[90vh]">
              <div className="hidden md:block bg-[#dcd6c9] relative">
                <img
                  src={active.image}
                  alt={active.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="relative flex flex-col overflow-y-auto max-h-[90vh]">
                <button
                  onClick={() => setActive(null)}
                  aria-label="Close"
                  className="absolute top-4 right-4 text-[#666] hover:text-[#b39869] transition-colors z-10"
                >
                  <X className="w-5 h-5" strokeWidth={1.2} />
                </button>

                {/* Mobile image */}
                <div className="md:hidden aspect-[3/4] bg-[#dcd6c9] relative">
                  <img
                    src={active.image}
                    alt={active.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>

                {/* Agent Details */}
                {(active.email || active.mobile || active.address) && (
                  <div className="bg-[#f5f1ea] px-8 md:px-10 py-6 border-b border-[#e5e0d5]">
                    <div className="inline-block mb-3 pb-1 border-b border-[#b39869]">
                      <span className="tracking-[0.35em] text-[10px] text-[#b39869] uppercase">Agent Details</span>
                    </div>
                    <dl className="text-[12px] text-[#333] space-y-2">
                      {active.email && (
                        <div className="flex gap-3">
                          <dt className="tracking-[0.2em] text-[10px] uppercase text-[#7a6b4e] w-16 pt-[3px]">Email</dt>
                          <dd>
                            <a href={`mailto:${active.email}`} className="hover:text-[#b39869] transition-colors break-all">
                              {active.email}
                            </a>
                          </dd>
                        </div>
                      )}
                      {active.mobile && (
                        <div className="flex gap-3">
                          <dt className="tracking-[0.2em] text-[10px] uppercase text-[#7a6b4e] w-16 pt-[3px]">Mobile</dt>
                          <dd>
                            <a href={`tel:${active.mobile.replace(/[^0-9+]/g, '')}`} className="hover:text-[#b39869] transition-colors">
                              {active.mobile}
                            </a>
                          </dd>
                        </div>
                      )}
                      {active.address && (
                        <div className="flex gap-3">
                          <dt className="tracking-[0.2em] text-[10px] uppercase text-[#7a6b4e] w-16 pt-[3px]">Address</dt>
                          <dd>{active.address}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                <div className="p-8 md:p-10">
                  <div className="inline-block mb-4 pb-1 border-b border-[#b39869]">
                    <span className="tracking-[0.35em] text-[10px] text-[#b39869] uppercase">Meet {active.name.split(' ')[0]}</span>
                  </div>
                  <DialogTitle asChild>
                    <h3
                      className="text-3xl md:text-4xl text-[#1a1a1a] leading-tight mb-2"
                      style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}
                    >
                      {active.name}
                    </h3>
                  </DialogTitle>
                  <p className="text-[#7a6b4e] text-[11px] tracking-[0.25em] uppercase mb-6">
                    {active.role}
                  </p>
                  <div className="text-[#444] text-sm md:text-[14px] leading-relaxed space-y-4">
                    {Array.isArray(active.bio)
                      ? active.bio.map((para) => <p key={para.slice(0, 40)}>{para}</p>)
                      : <p>{active.bio}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
